import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Instagram, X } from "lucide-react";
import heroImage from "@/assets/hero-bar.jpg";
import cocktailImage from "@/assets/cocktail-signature.jpg";
import barInterior from "@/assets/bar-interior.jpg";
import barCrowd from "@/assets/bar-crowd.jpg";
import djNight from "@/assets/dj-night.jpg";
import barFood from "@/assets/bar-food.jpg";

interface GalleryImage {
  id: string;
  title: string | null;
  image_url: string;
  is_featured: boolean;
}

// Fallback images in case database is empty
const fallbackImages = [
  { src: heroImage, alt: "Signature Cocktail with City View" },
  { src: cocktailImage, alt: "Crafted Cocktail Being Poured" },
  { src: barInterior, alt: "Tommy Hour Bar Interior" },
  { src: barCrowd, alt: "Happy Guests at Tommy Hour" },
  { src: djNight, alt: "DJ Night Atmosphere" },
  { src: barFood, alt: "Premium Bar Snacks" },
];

export function GallerySection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [images, setImages] = useState<{ src: string; alt: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGallery();
  }, []);

  // Map local asset paths to imported images
  const getImageSrc = (url: string) => {
    const assetMap: Record<string, string> = {
      '/assets/hero-bar.jpg': heroImage,
      '/assets/cocktail-signature.jpg': cocktailImage,
      '/assets/bar-interior.jpg': barInterior,
      '/assets/bar-crowd.jpg': barCrowd,
      '/assets/dj-night.jpg': djNight,
      '/assets/bar-food.jpg': barFood,
    };
    // If it's a local asset path, use the imported image
    if (url.startsWith('/assets/')) {
      return assetMap[url] || url;
    }
    // Otherwise use the URL directly (Supabase storage URLs)
    return url;
  };

  const fetchGallery = async () => {
    const { data, error } = await supabase
      .from('gallery_images')
      .select('*')
      .order('sort_order');

    if (!error && data && data.length > 0) {
      setImages(data.map(img => ({
        src: getImageSrc(img.image_url),
        alt: img.title || 'Gallery image'
      })));
    } else {
      setImages(fallbackImages);
    }
    setLoading(false);
  };

  return (
    <section id="gallery" className="py-24 md:py-32 relative overflow-hidden">
      <div className="container mx-auto px-4" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-primary font-medium tracking-[0.2em] text-sm mb-4">THE VIBE</p>
          <h2 className="section-heading text-foreground underline-gold">GALLERY</h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {loading ? (
            [1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={`aspect-square bg-card/50 animate-pulse rounded-lg ${i === 1 ? 'md:col-span-2 md:row-span-2' : ''}`} />
            ))
          ) : (
            images.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative group cursor-pointer overflow-hidden rounded-lg ${index === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
                onClick={() => setSelectedImage(image.src)}
              >
                <div className={`aspect-square ${index === 0 ? "md:aspect-auto md:h-full" : ""}`}>
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="text-center">
                    <Instagram className="w-8 h-8 text-primary mx-auto mb-2" />
                    <p className="text-foreground text-sm font-medium">View</p>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-12"
        >
          <a
            href="https://instagram.com/tommyhour"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 text-foreground/70 hover:text-primary transition-colors group"
          >
            <Instagram className="w-5 h-5" />
            <span className="font-medium">Follow @tommyhour</span>
            <span className="text-primary group-hover:translate-x-1 transition-transform">→</span>
          </a>
        </motion.div>
      </div>

      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-background/95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button onClick={() => setSelectedImage(null)} className="absolute top-6 right-6 text-foreground/70 hover:text-foreground">
            <X className="w-8 h-8" />
          </button>
          <img src={selectedImage} alt="Gallery Preview" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
        </motion.div>
      )}
    </section>
  );
}